import React, { createContext, FC, ReactNode, useContext } from 'react';

import { useSelector } from 'react-redux';

import { ENVIRONMENT_DEMO, ENVIRONMENT_PRODUCTION, ENVIRONMENT_STAGE } from '../../constants';
import { AppState } from '../../store/appState';
import { AuthenticationState } from '../../store/authentication/state';

type Environment = typeof ENVIRONMENT_STAGE | typeof ENVIRONMENT_DEMO | typeof ENVIRONMENT_PRODUCTION | undefined;

const Ctx = createContext<Environment>(undefined);
interface Props {
    children: ReactNode;
    environment?: Environment;
}
export const EnvironmentProvider: FC<Props> = ({ children, environment = undefined }) => {
    const authentication = useSelector(selectAuthentication);
    const env = environment ?? getEnvironment(authentication);
    return <Ctx.Provider value={env}>{children}</Ctx.Provider>;
};

export const EnvironmentConsumer = Ctx.Consumer;

function selectAuthentication(state: AppState): AuthenticationState {
    return state.authentication;
}

const mapRealmToEnv = {
    'connect-stage': ENVIRONMENT_STAGE,
    'connect-demo': ENVIRONMENT_DEMO,
    connect: ENVIRONMENT_PRODUCTION
};

function getEnvironment(authentication: AuthenticationState) {
    if (!authentication) {
        throw new Error('authentication object needs to be provided to return environment');
    }

    const realm = authentication.kc?.realm;
    const environment = realm ? mapRealmToEnv[realm] : undefined;

    if (!environment) {
        throw new Error(
            `Can't map realm '${realm}' to environment, maybe the key of an realm was updated or a realm was added`
        );
    }
    return environment;
}

export function useEnvironment() {
    const context = useContext(Ctx);
    if (!context) {
        throw new Error(`useEnvironment must be used within an EnvironmentProvider`);
    }
    return context;
}
